import DOMPurify from "dompurify";
import { load } from "js-yaml";
import MarkdownIt from "markdown-it";
import type { Article, FrontMatter } from "./types";
import { fileStem, normalizeSlug, stripOrderingPrefix } from "./slug";

const md = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true,
  breaks: true,
});

export function renderMarkdown(body: string): string {
  return DOMPurify.sanitize(md.render(body));
}

export function parseArticle(path: string, raw: string, now = new Date()): Article {
  const trimmed = raw.trim();
  if (!trimmed) {
    throw new Error("empty article");
  }

  const parsed = parseFrontMatter(trimmed);
  const frontMatter = parsed.data;
  const body = parsed.body.trim();
  if (!body) {
    throw new Error("article body is empty");
  }
  if (!frontMatter.title) {
    throw new Error("title is required");
  }

  const fallbackSlug = stripOrderingPrefix(fileStem(path));
  const slug = normalizeSlug(frontMatter.slug || fallbackSlug);
  if (!slug) {
    throw new Error("slug is required");
  }

  const publishedAt = normalizeDate(frontMatter.published_at, now);
  const updatedAt = normalizeDate(frontMatter.updated_at, new Date(publishedAt));
  const subtitle = frontMatter.subtitle || "";
  const excerpt = frontMatter.excerpt || subtitle;

  return {
    slug,
    title: frontMatter.title,
    subtitle,
    excerpt,
    cover_image: frontMatter.cover_image || "",
    tags: Array.isArray(frontMatter.tags) ? frontMatter.tags : [],
    published_at: publishedAt,
    updated_at: updatedAt,
    author: frontMatter.author || "Anonymous",
    body,
    html: renderMarkdown(body),
    word_count: body.split(/\s+/).filter(Boolean).length,
    series_slug: null,
    series_title: null,
    series_position: null,
    chapter_slug: null,
    chapter_title: null,
    chapter_position: null,
    prev_in_series: null,
    next_in_series: null,
  };
}

function parseFrontMatter(raw: string): { data: FrontMatter; body: string } {
  if (!raw.startsWith("---\n")) {
    return { data: {}, body: raw };
  }
  const closing = raw.indexOf("\n---\n", 4);
  if (closing === -1) {
    throw new Error("front matter missing closing delimiter");
  }
  const yaml = raw.slice(4, closing);
  const body = raw.slice(closing + 5);
  const data = load(yaml);
  if (!data || typeof data !== "object") {
    return { data: {}, body };
  }
  return { data: data as FrontMatter, body };
}

function normalizeDate(value: string | undefined, fallback: Date): string {
  if (!value) {
    return fallback.toISOString();
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`invalid date ${value}`);
  }
  return date.toISOString();
}
